import { Component, OnInit } from '@angular/core';
import moment from 'moment-timezone';

import { IEvent } from '@azavista/servicelib';
import { AbstractWidgetDirective, IWidgetComponentDefinition } from '@azavista/components/widgets';
import { IWidgetDataChanges, SharedService } from '../../../shared';
import { IClockWidgetAssetsConfig, ICountdownClockWidgetClickArgs, ICountdownClockWidgetSettings } from './interfaces';
import { CountdownClockWidgetService } from './countdown-clock-widget.service';
import { WidgetViewDetailsNavigationPaths } from '../interfaces';
import { WidgetsUtilsService } from '../widgets-utils.service';
import { TranslationKey } from '../../enums';
import { CommonDialogsService } from '../../common-dialogs.service';
import { WidgetCustomEventType } from '../enums';

@Component({
    templateUrl: 'countdown-clock-widget.component.html',
    styleUrls: ['countdown-clock-widget.component.css'],
    standalone: false
})
export class CountdownClockWidgetComponent extends AbstractWidgetDirective implements OnInit {
    isLoading = false;
    eventHasStarted = false;
    definitionSettings: ICountdownClockWidgetSettings;
    clockWidgetAssetsConfig: IClockWidgetAssetsConfig;
    isEventPredefined = false;
    event: IEvent;
    eventId: string;
    eventLocalStartTime = 0;
    isCountdownStarted = false;
    countdownRefreshInterval = 1000;
    eventNotAvailable = false;

    private moment = moment;

    constructor(
        private widgetSvc: CountdownClockWidgetService,
        private widgetsUtilsSvc: WidgetsUtilsService, private sharedSvc: SharedService,
        private commonDlgsSvc: CommonDialogsService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.definitionSettings = this.definition.settings;
        this.clockWidgetAssetsConfig = this.widgetSvc.createEmptyAssetsConfig();
        try {
            const contextEventId = this.definition.context?.eventId;
            if (contextEventId) {
                // The widget is inside event context
                await this.setEventFromContext(contextEventId);
            }

            super.subscribeToDataChanges({
                next: (newData?: IWidgetDataChanges) => {
                    this.dataChanged(newData);
                }
            });

            if (this.configMode) {
                // Use already loaded context event or load it from server
                this.event ||= await this.widgetsUtilsSvc.getEvent(this.definitionSettings.remote.event_id);
            } else {
                // Not in config mode
                const hasRemoteSettings = !this.widgetSvc.areRemoteSettingsEmpty(this.definitionSettings.remote);
                if (hasRemoteSettings) {
                    await this.loadEventAndStartCounting(this.definitionSettings.remote.event_id);
                } else {
                    // Not in config mode and no settings provided - emit settings are needed
                    this.settingsNeeded.next();
                }
            }
        } catch (err) {
            this.eventNotAvailable = true;
        }
        this.eventNotAvailable = !this.event;
        this.setCustomWidgetParams();
    }

    setCustomWidgetParams(): void {
        this.definition.viewButtonText = this.sharedSvc.translate(TranslationKey.goToEvent);
        this.definition.viewDetailsNavPath = WidgetViewDetailsNavigationPaths.event;
        this.definition.viewDetailsNavQueryParams = this.widgetsUtilsSvc.getViewDetailsNavQueryParamsForEvent(
            this.eventId || this.event?.id
        );
        this.onSettingsChanged();
    }

    onClick(): void {
        const args: ICountdownClockWidgetClickArgs = { eventId: this.definitionSettings.remote.event_id };
        this.customEvent.emit({ type: WidgetCustomEventType.click, data: args });
    }

    dataChanged(newData?: IWidgetDataChanges): void {
        if (!this.configMode && newData.settings && newData.settings.remote) {
            // Not config mode and remote settings were changed - reload data from server
            this.loadWidgetData(this.definition);
        }
    }

    async loadWidgetData(definition: IWidgetComponentDefinition): Promise<void> {
        this.definitionSettings = definition.settings;
        const eventId = (definition.settings as ICountdownClockWidgetSettings).remote.event_id;
        this.eventNotAvailable = !eventId;
        this.loadEventAndStartCounting(eventId);
        // We don't need to emit because our data is only local
        // definition.dataChangedSubject.next({ data: definition.data });
    }

    async loadEventAndStartCounting(eventId: string): Promise<void> {
        this.notifyLoading(true);
        this.event = await this.widgetsUtilsSvc.getEvent(eventId);
        if (this.event) {
            this.eventNotAvailable = false;
            this.startCounting(this.event.start, this.event.timezone);
        } else {
            // Stop couting since event is not loaded
            this.eventNotAvailable = true;
            this.stopCounting();
        }
        this.notifyLoading(false);
    }

    async setEventFromContext(contextEventId: string): Promise<void> {
        this.isEventPredefined = true;
        this.definitionSettings.remote.event_id = contextEventId;
        this.event = await this.widgetsUtilsSvc.getEvent(contextEventId);
    }

    startCounting(targetDate: string, timezone: string): void {
        const localDate = this.moment(targetDate).tz(timezone || '');
        this.eventLocalStartTime = localDate.toDate().getTime();
        this.eventHasStarted = false;
        this.isCountdownStarted = true;
        this.countdown();
    }

    countdown(): void {
        if (!this.isCountdownStarted) {
            return;
        }
        window.setTimeout(() => {
            const now = Date.now();
            const diff = this.eventLocalStartTime - now;
            if (diff > 0) {
                this.widgetSvc.modifyAssetsConfigByTimeDiff(diff, this.clockWidgetAssetsConfig);
            } else {
                // Event start date is in the past
                this.isCountdownStarted = false;
                this.eventHasStarted = true;
                return;
            }
            if (this.isCountdownStarted) {
                this.countdown();
            }
        }, this.countdownRefreshInterval);
    }

    stopCounting(): void {
        this.isCountdownStarted = false;
    }

    async onSelectEvent(): Promise<void> {
        const title = this.sharedSvc.translate(TranslationKey.events);
        const dlgResult = await this.commonDlgsSvc.showSelectEventsDialog(title, true);
        if (!dlgResult || dlgResult.length === 0) {
            return;
        }
        this.event = dlgResult[0] as IEvent;
        const eventId = this.event ? this.event.id : null;
        this.definitionSettings.remote.event_id = eventId;
        this.eventId = eventId;
        this.setCustomWidgetParams();
        this.onSettingsChanged();
    }

    onSettingsChanged(): void {
        const settingsAreValid = this.widgetSvc.areSettingsValid(this.definitionSettings);
        this.settingsChanged.next({
            definition: this.definition,
            settings: this.definitionSettings,
            settingsAreValid: settingsAreValid
        });
    }
}
